import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Wordmark } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const inputClass =
  "w-full bg-brand-ivory/50 border-brand-line rounded-xl px-4 py-3 h-auto text-brand-anthracite placeholder-[#A3988E] focus-visible:ring-2 focus-visible:ring-brand-champagne";

function AuthShell({ children }) {
  return (
    <div className="min-h-screen bg-brand-ivory flex flex-col lg:flex-row">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1599206676335-193c82b13c9e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Nail studio"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-brand-anthracite/25" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <div className="max-w-sm">
            <div className="h-px w-16 bg-brand-champagne mb-6" />
            <h2 className="font-serif text-3xl font-medium leading-snug">
              Semplice, elegante, sostenibile.
            </h2>
          </div>
        </div>
      </div>
      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-between p-5 sm:p-8">
          <Wordmark compact />
          <LanguageSwitcher />
        </div>
        <div className="flex-1 flex items-center justify-center px-5 pb-10">
          <div className="w-full max-w-md animate-fade-up">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { t, login, apiError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brand-anthracite">{t("auth.loginTitle")}</h1>
      <p className="mt-2 text-brand-taupe">{t("auth.welcomeSubtitle")}</p>
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label>{t("auth.email")}</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} data-testid="login-email" />
        </div>
        <div className="space-y-2">
          <Label>{t("auth.password")}</Label>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} data-testid="login-password" />
        </div>
        <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-12 rounded-xl bg-brand-anthracite text-brand-ivory hover:bg-brand-anthracite/90 text-base">
          {loading ? t("common.loading") : t("auth.signIn")}
        </Button>
      </form>
      <div className="mt-6 flex items-center justify-between text-sm">
        <Link to="/forgot" className="text-brand-taupe hover:text-brand-anthracite" data-testid="link-forgot">{t("auth.forgotPassword")}</Link>
        <Link to="/register" className="font-medium text-brand-anthracite underline decoration-brand-champagne underline-offset-4" data-testid="link-register">{t("auth.signUp")}</Link>
      </div>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { t, register, apiError, config } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!acceptTerms) { toast.error(t("pro.acceptTermsRequired")); return; }
    setLoading(true);
    try {
      await register({ ...form, accept_terms: true });
      navigate("/onboarding");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brand-anthracite">{t("auth.registerTitle")}</h1>
      <p className="mt-2 text-brand-taupe">{t("auth.welcomeTitle")}</p>
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
        <div className="space-y-2">
          <Label>{t("auth.email")}</Label>
          <Input type="email" required value={form.email} onChange={upd("email")} className={inputClass} data-testid="register-email" />
        </div>
        <div className="space-y-2">
          <Label>{t("auth.password")}</Label>
          <Input type="password" required minLength={6} value={form.password} onChange={upd("password")} className={inputClass} data-testid="register-password" placeholder={t("auth.minPassword")} />
        </div>
        <label className="flex items-start gap-2 text-sm text-brand-anthracite">
          <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} data-testid="register-accept-terms" className="mt-1 accent-brand-anthracite" />
          <span>{t("pro.acceptTermsPre")}{" "}
            <a href={config.terms_url || "#"} target="_blank" rel="noreferrer" className="underline decoration-brand-champagne" data-testid="register-terms-link">{t("pro.acceptTermsLink")}</a>.
            {config.privacy_url ? <> · <a href={config.privacy_url} target="_blank" rel="noreferrer" className="underline decoration-brand-champagne" data-testid="register-privacy-link">{t("pro.privacy")}</a></> : null}
          </span>
        </label>
        <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full h-12 rounded-xl bg-brand-anthracite text-brand-ivory hover:bg-brand-anthracite/90 text-base">
          {loading ? t("common.loading") : t("auth.createAccount")}
        </Button>
      </form>
      <div className="mt-6 text-center text-sm text-brand-taupe">
        {t("auth.haveAccount")}{" "}
        <Link to="/login" className="font-medium text-brand-anthracite underline decoration-brand-champagne underline-offset-4" data-testid="link-login">{t("auth.signIn")}</Link>
      </div>
    </AuthShell>
  );
}

export function ForgotPage() {
  const { t, apiError } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const requestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      // Uniform public confirmation: never reveals whether the account exists.
      setSent(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brand-anthracite">{t("auth.forgotTitle")}</h1>
      {sent ? (
        <div className="mt-8 space-y-4" data-testid="forgot-sent">
          <h2 className="font-serif text-xl text-brand-anthracite">{t("auth.forgotSentTitle")}</h2>
          <p className="text-brand-taupe">{t("auth.forgotSentDesc")}</p>
        </div>
      ) : (
        <>
          <p className="mt-2 text-brand-taupe">{t("auth.forgotSubtitle")}</p>
          <form onSubmit={requestReset} className="mt-8 space-y-5" data-testid="forgot-form">
            <div className="space-y-2">
              <Label htmlFor="forgot-email">{t("auth.email")}</Label>
              <Input id="forgot-email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} data-testid="forgot-email" />
            </div>
            <Button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full h-12 rounded-xl bg-brand-anthracite text-brand-ivory">
              {loading ? t("common.loading") : t("auth.findAccount")}
            </Button>
          </form>
        </>
      )}
      <div className="mt-6 text-center text-sm">
        <Link to="/login" className="text-brand-taupe hover:text-brand-anthracite" data-testid="link-back-login">{t("auth.backToLogin")}</Link>
      </div>
    </AuthShell>
  );
}

export function ResetPage() {
  const { t, apiError } = useAuth();
  const [searchParams] = useSearchParams();
  // Transient only: the token lives in the URL and the request body, never in
  // storage, logs, or analytics.
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [mismatch, setMismatch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!token) {
    return (
      <AuthShell>
        <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brand-anthracite">{t("auth.resetTitle")}</h1>
        <p className="mt-4 text-brand-taupe" data-testid="reset-invalid">{t("auth.resetInvalid")}</p>
        <div className="mt-6 text-center text-sm">
          <Link to="/forgot" className="text-brand-taupe hover:text-brand-anthracite" data-testid="link-back-forgot">{t("auth.forgotPassword")}</Link>
        </div>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell>
        <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brand-anthracite">{t("auth.resetTitle")}</h1>
        <p className="mt-4 text-brand-anthracite" data-testid="reset-done">{t("auth.resetDoneDesc")}</p>
        <Link to="/login" className="mt-6 block"><Button className="w-full h-12 rounded-xl bg-brand-anthracite text-brand-ivory" data-testid="reset-done-login">{t("auth.backToLogin")}</Button></Link>
      </AuthShell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setMismatch(true); return; }
    setMismatch(false);
    setLoading(true);
    setFailed(false);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setFailed(true);
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brand-anthracite">{t("auth.resetTitle")}</h1>
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
        <div className="space-y-2">
          <Label htmlFor="reset-new-password">{t("auth.newPassword")}</Label>
          <Input id="reset-new-password" type="password" required minLength={6} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} data-testid="reset-password" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="reset-confirm-password">{t("auth.confirmPassword")}</Label>
          <Input id="reset-confirm-password" type="password" required minLength={6} autoComplete="new-password" aria-invalid={mismatch || undefined} value={confirm} onChange={(e) => { setConfirm(e.target.value); setMismatch(false); }} className={inputClass} data-testid="reset-confirm" />
          {mismatch && <p className="text-sm text-red-600" role="alert" data-testid="reset-mismatch">{t("auth.resetPasswordMismatch")}</p>}
          {failed && <p className="text-sm text-red-600" role="alert" data-testid="reset-error">{t("auth.resetInvalid")}</p>}
        </div>
        <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full h-12 rounded-xl bg-brand-anthracite text-brand-ivory">
          {loading ? t("common.loading") : t("auth.resetTitle")}
        </Button>
      </form>
      <div className="mt-6 text-center text-sm">
        <Link to="/login" className="text-brand-taupe hover:text-brand-anthracite" data-testid="link-back-login">{t("auth.backToLogin")}</Link>
      </div>
    </AuthShell>
  );
}
